/**
 * Sales.jsx — CRM Comercial (Fase A)
 *
 * Layout:
 *   [Dashboard (counters + hoy/atrasados)] tab
 *   [Leads (lista + ficha 360°)]           tab
 *   [Cotizaciones]                          tab
 *   [Seguimientos]                          tab
 *
 * Design goal: register a call in ≤ 2 minutes. The primary CTAs are pinned at
 * the top of every tab and the ficha 360° condenses the whole history into a
 * single vertical timeline the sales rep can scan in seconds.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import {
  PhoneCall, Users, FileText, ListChecks, Plus, X, Search, Clock,
  AlertTriangle, CheckCircle2, TrendingUp, ArrowRight, MessageSquare,
  ChevronRight, Calendar, Mail, MapPin, Building2, ClipboardList, Sparkles,
  BarChart3, Globe2,
} from "lucide-react";
import { CountrySelect, findCountry, countryLabel } from "@/components/GeoSelect";

/* ==================== Helpers ==================== */
const STATE_TONE = {
  nueva: "bg-slate-100 text-slate-700",
  cotizada: "bg-blue-100 text-blue-700",
  pendiente: "bg-amber-100 text-amber-700",
  seguimiento: "bg-violet-100 text-violet-700",
  aceptada: "bg-emerald-100 text-emerald-700",
  confirmada: "bg-emerald-600 text-white",
  rechazada: "bg-rose-100 text-rose-700",
  perdida: "bg-slate-800 text-white",
  cancelada: "bg-slate-200 text-slate-500",
};

const TASK_TONE = {
  llamar: "bg-red-100 text-red-700",
  enviar_precio: "bg-blue-100 text-blue-700",
  enviar_docs: "bg-indigo-100 text-indigo-700",
  esperar_confirmacion: "bg-amber-100 text-amber-700",
  confirmar_envio: "bg-emerald-100 text-emerald-700",
  recontactar: "bg-violet-100 text-violet-700",
  otro: "bg-slate-100 text-slate-700",
};

/** Translate a CRM task type using the i18n dictionary.  `t` must be the
 * translator function from `useI18n()`.  Falls back to the raw underscore-free
 * key so unknown types still render sensibly. */
const taskTypeLabel = (type, t) => {
  if (!type) return "";
  const key = `crm_type_${type}`;
  const v = t(key);
  return v !== key ? v : type.replace(/_/g, " ");
};

/** Translate a CRM quote state (nueva/cotizada/…) */
const quoteStateLabel = (state, t) => {
  if (!state) return "";
  const key = `crm_state_${state}`;
  const v = t(key);
  return v !== key ? v : state;
};

// Convert a JS Date to the value expected by <input type="datetime-local">
const toLocalDT = (d) => {
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};
const fromLocalDT = (s) => (s ? new Date(s).toISOString() : "");

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const fmtMoney = (n, cur = "EUR") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: cur || "EUR", maximumFractionDigits: 0 }).format(+n || 0);

/* ==================== Root ==================== */
export default function Sales() {
  const { api, user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState("dashboard");

  const TABS = [
    { key: "dashboard", label: t("dashboard"), icon: TrendingUp },
    { key: "leads", label: t("clients"), icon: Users },
    { key: "quotes", label: t("crm_quotes") || "Cotizaciones", icon: FileText },
    { key: "tasks", label: t("crm_followups") || "Seguimientos", icon: ListChecks },
    { key: "stats", label: t("crm_stats") || "Estadísticas", icon: BarChart3 },
  ];

  return (
    <div className="space-y-4">
      {/* Tabs — horizontally scrollable on mobile */}
      <div className="bg-white border border-slate-200 rounded-xl p-1.5 flex gap-1 overflow-x-auto" data-testid="crm-tabs">
        {TABS.map(x => (
          <button
            key={x.key}
            data-testid={`crm-tab-${x.key}`}
            onClick={() => setTab(x.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
              tab === x.key ? "bg-red-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <x.icon className="w-4 h-4" />
            {x.label}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <Dashboard api={api} t={t} user={user} onGo={setTab} />}
      {tab === "leads" && <LeadsTab api={api} t={t} user={user} />}
      {tab === "quotes" && <QuotesTab api={api} t={t} />}
      {tab === "tasks" && <TasksTab api={api} t={t} />}
      {tab === "stats" && <StatsTab api={api} t={t} />}
    </div>
  );
}

/* ==================== Dashboard ==================== */
function Dashboard({ api, t, user, onGo }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/crm/dashboard"); setData(r.data); }
    catch (err) { console.warn("Sales dashboard load failed", err?.response?.status || err?.message); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    if (!window.confirm(t("crm_seed_confirm") || "¿Cargar datos de ejemplo del CRM?")) return;
    setBusy(true);
    try {
      const r = await api.post("/crm/seed");
      if (r.data.seeded) toast.success(t("saved"));
      else toast.info("Ya hay datos");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-8 text-center text-slate-400">…</div>;

  const c = data.counters;
  const cards = [
    { k: "new_leads", label: t("crm_new_leads") || "Nuevos clientes (7d)", val: c.new_leads, tone: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: Users, to: "leads" },
    { k: "calls_today", label: t("crm_calls_today") || "Llamadas hoy", val: c.calls_today, tone: "bg-blue-50 text-blue-700 border-blue-200", icon: PhoneCall, to: "leads" },
    { k: "quotes_pending", label: t("crm_quotes_pending") || "Cot. pendientes", val: c.quotes_pending, tone: "bg-amber-50 text-amber-800 border-amber-200", icon: FileText, to: "quotes" },
    { k: "tasks_today", label: t("crm_tasks_today") || "Seguim. hoy", val: c.tasks_today, tone: "bg-red-50 text-red-700 border-red-200", icon: Clock, to: "tasks" },
    { k: "tasks_overdue", label: t("crm_tasks_overdue") || "Atrasados", val: c.tasks_overdue, tone: "bg-rose-50 text-rose-700 border-rose-200", icon: AlertTriangle, to: "tasks" },
    { k: "tasks_upcoming", label: t("crm_tasks_upcoming") || "Próximos 7d", val: c.tasks_upcoming, tone: "bg-violet-50 text-violet-700 border-violet-200", icon: Calendar, to: "tasks" },
    { k: "quotes_won", label: t("crm_quotes_won") || "Aceptadas", val: c.quotes_won, tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, to: "quotes" },
    { k: "quotes_lost", label: t("crm_quotes_lost") || "Perdidas", val: c.quotes_lost, tone: "bg-slate-100 text-slate-500 border-slate-200", icon: X, to: "quotes" },
  ];

  return (
    <div className="space-y-4">
      {user.role === "admin" && c.new_leads === 0 && c.calls_today === 0 && (
        <div className="bg-gradient-to-r from-red-50 to-amber-50 border border-red-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-5 h-5 text-red-600 shrink-0" />
            <span className="text-sm text-slate-700"><b>{t("crm_seed_hint_title") || "Aún no hay datos"}.</b> {t("crm_seed_hint_desc") || "Carga 3 clientes de ejemplo con llamadas y cotizaciones para explorar el CRM."}</span>
          </div>
          <button
            data-testid="crm-seed-btn"
            onClick={seed}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {t("crm_seed_btn") || "Cargar ejemplos"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map(x => (
          <button
            key={x.k}
            data-testid={`crm-kpi-${x.k}`}
            onClick={() => onGo(x.to)}
            className={`text-start p-3 rounded-xl border-2 ${x.tone} hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between mb-2">
              <x.icon className="w-4 h-4 opacity-70" />
              <ArrowRight className="w-3 h-3 opacity-50" />
            </div>
            <div className="text-2xl font-display font-bold">{x.val}</div>
            <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{x.label}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TaskList title={t("crm_tasks_today") || "Seguimientos de hoy"} rows={data.today_tasks} api={api} onDone={load} accent="red" testid="crm-today" empty={t("crm_no_tasks_today") || "No hay seguimientos para hoy."} t={t} />
        <TaskList title={t("crm_tasks_overdue") || "Atrasados"} rows={data.overdue_tasks} api={api} onDone={load} accent="rose" testid="crm-overdue" empty={t("crm_no_overdue") || "No hay atrasos. Buen trabajo."} t={t} />
      </div>
    </div>
  );
}

function TaskList({ title, rows, api, onDone, accent, testid, empty, t }) {
  const close = async (id) => {
    try { await api.patch(`/crm/tasks/${id}`, null, { params: { estado: "hecha" } }); onDone(); toast.success("OK"); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-3 border-b border-slate-100 bg-${accent}-50/60`}>
        <h3 className="font-display font-semibold text-slate-900 text-sm">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-400">{empty}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map(task => (
            <li key={task.id} className="px-4 py-3 flex items-center gap-3">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TASK_TONE[task.tipo] || "bg-slate-100 text-slate-700"}`}>
                {taskTypeLabel(task.tipo, t)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{task.lead_nombre}</div>
                <div className="text-xs text-slate-500 truncate">{task.detalle} · {fmtWhen(task.fecha)}</div>
              </div>
              <button
                data-testid={`crm-task-done-${task.id}`}
                onClick={() => close(task.id)}
                className="text-xs font-semibold px-2 py-1 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white"
                title={t("crm_mark_done") || "Marcar hecha"}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ==================== Leads (clients) ==================== */
function LeadsTab({ api, t, user }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);   // id of lead being viewed
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get("/crm/leads", { params: q ? { q } : {} }); setRows(r.data); }
    catch (err) { console.warn("CRM leads load failed", err?.response?.status || err?.message); }
  }, [api, q]);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return <LeadDetail api={api} t={t} user={user} leadId={selected} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
          <input
            data-testid="crm-leads-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("crm_leads_search_ph") || "Buscar por nombre, teléfono, email..."}
            className="w-full ps-9 pe-3 py-2.5 border border-slate-300 rounded-xl text-sm outline-none focus:border-red-600 bg-white"
          />
        </div>
        <button
          data-testid="crm-lead-new-btn"
          onClick={() => setShowForm(true)}
          className="bg-red-600 hover:bg-red-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> {t("crm_new_request") || "Añadir Nueva Solicitud"}
        </button>
      </div>

      {showForm && <LeadForm api={api} t={t} onClose={() => setShowForm(false)} onSaved={(l) => { setShowForm(false); load(); setSelected(l.id); }} />}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="crm-leads-empty">
            {q ? (t("crm_no_results") || "Sin resultados") : (t("crm_no_leads") || "Todavía no hay clientes registrados.")}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="crm-leads-list">
            {rows.map(l => (
              <li key={l.id}>
                <button
                  data-testid={`crm-lead-${l.id}`}
                  onClick={() => setSelected(l.id)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-start"
                >
                  <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold shrink-0">
                    {(l.nombre_empresa || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{l.nombre_empresa}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {l.persona_contacto || "—"} · {l.telefono || l.email || "—"}
                    </div>
                  </div>
                  <div className="hidden sm:block text-xs text-slate-400">{l.ciudad || l.pais}</div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const EMPTY_LEAD = {
  nombre_empresa: "", persona_contacto: "", telefono: "", email: "",
  direccion: "", ciudad: "", pais: "", observaciones: "", origen: "llamada",
};

/**
 * LeadForm — "Añadir Nueva Solicitud"
 * Captures BOTH the contact info (lead) and the route/cargo brief
 * (same fields as the Cotizar calculator). On submit:
 *   1) POST /crm/leads       (creates the lead with contact info)
 *   2) POST /crm/quotes      (creates the linked quote with route + cargo)
 * If `initial` is set we skip the quote step and only patch the lead.
 */
function LeadForm({ api, t, onClose, onSaved, initial = null }) {
  const [contact, setContact] = useState(initial ? {
    persona_contacto: initial.persona_contacto || "",
    telefono: initial.telefono || "",
    email: initial.email || "",
    nombre_empresa: initial.nombre_empresa || "",
    direccion: initial.direccion || "",
    ciudad: initial.ciudad || "",
    pais: initial.pais || "",
    observaciones: initial.observaciones || "",
    origen: initial.origen || "llamada",
  } : { ...EMPTY_LEAD });

  // Route + cargo (only for new requests)
  const [countries, setCountries] = useState([]);
  const [route, setRoute] = useState({
    pais_origen: "", ciudad_origen: "",
    pais_destino: "", ciudad_destino: "",
    modo: "aereo",
    tipo_carga: "kg",
    peso_kg: 0, cbm: 0, bultos: 0,
    largo: 0, ancho: 0, alto: 0,
    container_type: "20ft", peso_contenedor: 0, peso_mercancia: 0,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (initial) return;
    api.get("/countries").then(r => setCountries((r.data || []).filter(c => c.activo))).catch(() => {});
  }, [api, initial]);

  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  const cargoOptions = useMemo(() => {
    if (route.modo === "maritimo") return [{ v: "cbm", l: t("cargo_cbm") }, { v: "fcl", l: t("fcl") || "FCL" }, { v: "kg", l: t("cargo_kg") }];
    if (route.modo === "terrestre") return [{ v: "pallets", l: t("cargo_pallets") }, { v: "cbm", l: t("cargo_cbm") }, { v: "kg", l: t("cargo_kg") }];
    if (route.modo === "courier") return [{ v: "kg", l: t("cargo_kg") }];
    return [{ v: "kg", l: t("cargo_kg") }, { v: "cbm", l: t("cargo_cbm") }];
  }, [route.modo, t]);

  useEffect(() => {
    if (!cargoOptions.find(o => o.v === route.tipo_carga)) {
      setRoute(r => ({ ...r, tipo_carga: cargoOptions[0].v }));
    }
    // eslint-disable-next-line
  }, [route.modo]);

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.persona_contacto.trim()) { toast.error(t("crm_contact_name") || "Nombre del cliente"); return; }
    if (!contact.telefono.trim()) { toast.error(t("phone")); return; }
    if (!initial) {
      if (!route.pais_origen) { toast.error(t("crm_from_country") || "País de origen"); return; }
      if (!route.pais_destino) { toast.error(t("crm_to_country") || "País de destino"); return; }
    }
    setBusy(true);
    try {
      // Company falls back to contact name when the caller didn't give one
      const leadPayload = {
        ...contact,
        nombre_empresa: contact.nombre_empresa.trim() || contact.persona_contacto.trim(),
        pais: contact.pais || route.pais_origen || "",
      };
      let lead;
      if (initial) {
        await api.patch(`/crm/leads/${initial.id}`, leadPayload);
        lead = { id: initial.id };
      } else {
        const r = await api.post("/crm/leads", leadPayload);
        lead = r.data;
        // Auto-create the linked route brief so it feeds the CRM stats.
        try {
          await api.post("/crm/quotes", {
            lead_id: lead.id,
            servicio: `${route.pais_origen} → ${route.pais_destino}`,
            pais_origen: route.pais_origen,
            ciudad_origen: route.ciudad_origen,
            pais_destino: route.pais_destino,
            ciudad_destino: route.ciudad_destino,
            modo: route.modo,
            tipo_carga: route.tipo_carga,
            peso_kg: +route.peso_kg || 0,
            cbm: +route.cbm || 0,
            bultos: +route.bultos || 0,
            container_type: route.tipo_carga === "fcl" ? route.container_type : "",
            peso_contenedor: route.tipo_carga === "fcl" ? (+route.peso_contenedor || 0) : 0,
            peso_mercancia: route.tipo_carga === "fcl" ? (+route.peso_mercancia || 0) : 0,
            descripcion: [
              route.tipo_carga && `${t("cargo_type") || "Tipo"}: ${route.tipo_carga}`,
              route.tipo_carga === "fcl" && `${route.container_type} · ${((+route.peso_contenedor||0)+(+route.peso_mercancia||0)).toLocaleString()} kg`,
              route.largo || route.ancho || route.alto ? `${route.largo}×${route.ancho}×${route.alto} cm` : "",
            ].filter(Boolean).join(" · "),
            estado: "nueva",
          });
        } catch (err) { console.warn("Auto-quote creation failed (lead saved anyway)", err?.response?.status || err?.message); }
      }
      toast.success(t("saved"));
      onSaved(lead);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="crm-lead-form">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-display font-semibold text-lg flex items-center gap-2">
            <Plus className="w-4 h-4 text-red-600" />
            {initial ? t("edit") : (t("crm_new_request") || "Añadir Nueva Solicitud")}
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* ---- Contact ---- */}
          <section className="space-y-3">
            <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> {t("crm_request_contact") || "Datos de contacto"}
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("crm_contact_name") || "Nombre del cliente"} *</label>
                <input required data-testid="lead-persona" value={contact.persona_contacto}
                  onChange={(e) => setContact({ ...contact, persona_contacto: e.target.value })}
                  className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("phone")} *</label>
                <input required data-testid="lead-tel" value={contact.telefono}
                  onChange={(e) => setContact({ ...contact, telefono: e.target.value })}
                  className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("email")}</label>
                <input type="email" data-testid="lead-email" value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("crm_source") || "Origen"}</label>
                <select data-testid="lead-origen" value={contact.origen}
                  onChange={(e) => setContact({ ...contact, origen: e.target.value })}
                  className={inp}>
                  <option value="llamada">{t("crm_origin_call") || "Llamada"}</option>
                  <option value="web">{t("crm_origin_web") || "Web"}</option>
                  <option value="referido">{t("crm_origin_referral") || "Referido"}</option>
                  <option value="feria">{t("crm_origin_trade_show") || "Feria"}</option>
                  <option value="otro">{t("crm_origin_other") || "Otro"}</option>
                </select>
              </div>
            </div>
          </section>

          {/* ---- Route (new request only) ---- */}
          {!initial && (
            <section className="space-y-3 rounded-xl bg-slate-50 border border-slate-200 p-3">
              <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5" /> {t("crm_request_route") || "Ruta del envío"}
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={lbl}>{t("crm_from_country") || "País de origen"} *</label>
                  <CountrySelect countries={countries} value={route.pais_origen}
                    onChange={(v) => setRoute({ ...route, pais_origen: v })}
                    testid="lead-pais-origen" className={inp} required />
                </div>
                <div>
                  <label className={lbl}>{t("crm_to_country") || "País de destino"} *</label>
                  <CountrySelect countries={countries} value={route.pais_destino}
                    onChange={(v) => setRoute({ ...route, pais_destino: v })}
                    testid="lead-pais-destino" className={inp} required />
                </div>
                <div>
                  <label className={lbl}>{t("origin_city") || "Ciudad origen"}</label>
                  <input data-testid="lead-ciudad-origen" value={route.ciudad_origen}
                    onChange={(e) => setRoute({ ...route, ciudad_origen: e.target.value })}
                    className={inp} />
                </div>
                <div>
                  <label className={lbl}>{t("destination_city") || "Ciudad destino"}</label>
                  <input data-testid="lead-ciudad-destino" value={route.ciudad_destino}
                    onChange={(e) => setRoute({ ...route, ciudad_destino: e.target.value })}
                    className={inp} />
                </div>
              </div>
              <div>
                <label className={lbl}>{t("crm_shipping_mode") || "Forma de envío"} *</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { v: "aereo", l: t("aereo") || "Aéreo" },
                    { v: "maritimo", l: t("maritimo") || "Marítimo" },
                    { v: "terrestre", l: t("terrestre") || "Terrestre" },
                    { v: "courier", l: t("courier") || "Courier" },
                  ].map(m => (
                    <button
                      key={m.v} type="button" data-testid={`lead-modo-${m.v}`}
                      onClick={() => setRoute({ ...route, modo: m.v })}
                      className={`px-2 py-2 border-2 rounded-lg text-xs font-semibold transition-all ${
                        route.modo === m.v ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                    >{m.l}</button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* ---- Cargo (new request only) ---- */}
          {!initial && (
            <section className="space-y-3 rounded-xl bg-white border border-slate-200 p-3">
              <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" /> {t("crm_request_cargo") || "Datos de la carga"}
              </h4>
              <div>
                <label className={lbl}>{t("cargo_type") || "Tipo de carga"}</label>
                <select data-testid="lead-tipo-carga" value={route.tipo_carga}
                  onChange={(e) => setRoute({ ...route, tipo_carga: e.target.value })}
                  className={inp}>
                  {cargoOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>

              {route.tipo_carga === "fcl" ? (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
                  <label className="block text-xs font-semibold text-red-900">{t("container_type") || "Tipo de contenedor"}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: "20ft", l: t("container_20") || "20'" },
                      { v: "40ft", l: t("container_40") || "40'" },
                      { v: "40hc", l: t("container_40hc") || "40 HC" },
                    ].map(c => (
                      <button
                        key={c.v} type="button" data-testid={`lead-container-${c.v}`}
                        onClick={() => setRoute({ ...route, container_type: c.v })}
                        className={`px-2 py-2 border-2 rounded-lg text-xs font-semibold transition-all ${
                          route.container_type === c.v ? "border-red-600 bg-white text-red-700" : "border-transparent bg-white/50 hover:bg-white text-slate-600"
                        }`}
                      >{c.l}</button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className={lbl}>{t("container_weight") || "Peso contenedor"}</label>
                      <input type="number" step="0.1" min="0" data-testid="lead-peso-contenedor"
                        value={route.peso_contenedor}
                        onChange={(e) => setRoute({ ...route, peso_contenedor: +e.target.value })}
                        className={`${inp} bg-white`} />
                    </div>
                    <div>
                      <label className={lbl}>{t("cargo_weight") || "Peso mercancía"}</label>
                      <input type="number" step="0.1" min="0" data-testid="lead-peso-mercancia"
                        value={route.peso_mercancia}
                        onChange={(e) => setRoute({ ...route, peso_mercancia: +e.target.value })}
                        className={`${inp} bg-white`} />
                    </div>
                  </div>
                  <p className="text-xs text-red-800">
                    {t("total_weight_fcl") || "Peso total"}: <b className="tabular-nums">{((+route.peso_contenedor||0)+(+route.peso_mercancia||0)).toLocaleString()} kg</b>
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={lbl}>{t("weight_kg") || "Peso (kg)"}</label>
                      <input type="number" step="0.1" min="0" data-testid="lead-peso"
                        value={route.peso_kg}
                        onChange={(e) => setRoute({ ...route, peso_kg: +e.target.value })}
                        className={inp} />
                    </div>
                    <div>
                      <label className={lbl}>CBM</label>
                      <input type="number" step="0.01" min="0" data-testid="lead-cbm"
                        value={route.cbm}
                        onChange={(e) => setRoute({ ...route, cbm: +e.target.value })}
                        className={inp} />
                    </div>
                    <div>
                      <label className={lbl}>{t("packages") || "Bultos"}</label>
                      <input type="number" step="1" min="0" data-testid="lead-bultos"
                        value={route.bultos}
                        onChange={(e) => setRoute({ ...route, bultos: +e.target.value })}
                        className={inp} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={lbl}>{t("length_cm") || "Largo (cm)"}</label>
                      <input type="number" step="0.1" min="0" data-testid="lead-largo"
                        value={route.largo}
                        onChange={(e) => setRoute({ ...route, largo: +e.target.value })}
                        className={inp} />
                    </div>
                    <div>
                      <label className={lbl}>{t("width_cm") || "Ancho (cm)"}</label>
                      <input type="number" step="0.1" min="0" data-testid="lead-ancho"
                        value={route.ancho}
                        onChange={(e) => setRoute({ ...route, ancho: +e.target.value })}
                        className={inp} />
                    </div>
                    <div>
                      <label className={lbl}>{t("height_cm") || "Alto (cm)"}</label>
                      <input type="number" step="0.1" min="0" data-testid="lead-alto"
                        value={route.alto}
                        onChange={(e) => setRoute({ ...route, alto: +e.target.value })}
                        className={inp} />
                    </div>
                  </div>
                </>
              )}
            </section>
          )}

          {/* ---- Optional extras ---- */}
          <section className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("crm_company") || "Empresa (opcional)"}</label>
                <input data-testid="lead-nombre" value={contact.nombre_empresa}
                  onChange={(e) => setContact({ ...contact, nombre_empresa: e.target.value })}
                  className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("city")}</label>
                <input data-testid="lead-ciudad" value={contact.ciudad}
                  onChange={(e) => setContact({ ...contact, ciudad: e.target.value })}
                  className={inp} />
              </div>
            </div>
            <div>
              <label className={lbl}>{t("crm_notes") || "Observaciones"}</label>
              <textarea data-testid="lead-notas" rows={2}
                value={contact.observaciones}
                onChange={(e) => setContact({ ...contact, observaciones: e.target.value })}
                className={inp} />
            </div>
          </section>
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">{t("cancel")}</button>
          <button type="submit" data-testid="lead-save" disabled={busy}
            className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50">
            {busy ? "..." : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ==================== Lead 360° detail ==================== */
function LeadDetail({ api, t, user, leadId, onBack }) {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);  // "call" | "quote" | "task" | "edit"

  const load = useCallback(async () => {
    try { const r = await api.get(`/crm/leads/${leadId}`); setData(r.data); }
    catch (e) { toast.error("No encontrado"); onBack(); }
  }, [api, leadId, onBack]);
  useEffect(() => { load(); }, [load]);

  // Merge calls + quotes + tasks into one chronological timeline
  // (must be declared BEFORE the early return so hook order stays stable)
  const timeline = useMemo(() => {
    if (!data) return [];
    const items = [
      ...(data.calls || []).map(c => ({ kind: "call", fecha: c.fecha, ...c })),
      ...(data.quotes || []).map(q => ({ kind: "quote", fecha: q.created_at, ...q })),
      ...(data.tasks || []).map(x => ({ kind: "task", fecha: x.fecha, ...x })),
    ];
    return items.sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
  }, [data]);

  if (!data) return <div className="p-8 text-center text-slate-400">…</div>;
  const l = data.lead;

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="lead-back">
        ← {t("back") || "Volver"}
      </button>

      {/* Lead header */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-red-100 text-red-700 flex items-center justify-center text-xl font-display font-bold shrink-0">
            {(l.nombre_empresa || "?")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-semibold text-xl text-slate-900">{l.nombre_empresa}</h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
              {l.persona_contacto && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {l.persona_contacto}</span>}
              {l.telefono && <a href={`tel:${l.telefono}`} className="flex items-center gap-1 hover:text-red-600"><PhoneCall className="w-3 h-3" /> {l.telefono}</a>}
              {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-red-600"><Mail className="w-3 h-3" /> {l.email}</a>}
              {(l.ciudad || l.pais) && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {[l.ciudad, l.pais].filter(Boolean).join(", ")}</span>}
            </div>
            {l.observaciones && <p className="text-sm text-slate-700 mt-2">{l.observaciones}</p>}
          </div>
          <button onClick={() => setModal("edit")} data-testid="lead-edit" className="text-xs font-semibold px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-100">{t("edit")}</button>
        </div>
      </div>

      {/* Quick actions — Cotización is the primary action */}
      <div className="space-y-2">
        <button
          data-testid="lead-add-quote"
          onClick={() => setModal("quote")}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-sm text-sm sm:text-base"
        >
          <FileText className="w-5 h-5" /> {t("crm_new_quote") || "Cotización"}
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="lead-add-call" onClick={() => setModal("call")} className="flex items-center justify-center gap-2 py-2.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl text-blue-700 font-semibold text-xs">
            <PhoneCall className="w-4 h-4" /> {t("crm_log_call") || "Llamada"}
          </button>
          <button data-testid="lead-add-task" onClick={() => setModal("task")} className="flex items-center justify-center gap-2 py-2.5 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-xl text-violet-700 font-semibold text-xs">
            <ListChecks className="w-4 h-4" /> {t("crm_add_task") || "Seguimiento"}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-red-600" /> {t("crm_timeline") || "Historial 360°"}</h3>
          <span className="text-xs text-slate-400">{timeline.length}</span>
        </div>
        {timeline.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">{t("crm_no_history") || "Aún no hay historial. Registra la primera llamada."}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {timeline.map((it, i) => <TimelineItem key={`${it.kind}-${it.id}-${i}`} item={it} t={t} api={api} onDone={load} />)}
          </ul>
        )}
      </div>

      {modal === "call" && <CallForm api={api} t={t} lead={l} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === "quote" && <QuoteForm api={api} t={t} lead={l} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === "task" && <TaskForm api={api} t={t} lead={l} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === "edit" && <LeadForm api={api} t={t} initial={l} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function TimelineItem({ item, t, api, onDone }) {
  const closeTask = async () => {
    try { await api.patch(`/crm/tasks/${item.id}`, null, { params: { estado: "hecha" } }); onDone(); toast.success("OK"); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  if (item.kind === "call") {
    return (
      <li className="px-4 py-3 flex gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0"><PhoneCall className="w-4 h-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-400">{fmtWhen(item.fecha)} · {item.empleado_nombre}</div>
          <div className="text-sm font-medium text-slate-900">{item.solicitud || (t(`crm_reason_${item.motivo}`) !== `crm_reason_${item.motivo}` ? t(`crm_reason_${item.motivo}`) : item.motivo)}</div>
          {item.informacion && <div className="text-xs text-slate-600 mt-1">{item.informacion}</div>}
          {item.proxima_accion && <div className="text-xs text-violet-600 mt-1">→ {item.proxima_accion} {item.proxima_fecha && `(${fmtWhen(item.proxima_fecha)})`}</div>}
        </div>
      </li>
    );
  }
  if (item.kind === "quote") {
    return (
      <li className="px-4 py-3 flex gap-3">
        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-xs text-slate-400">{fmtWhen(item.fecha)} · {item.numero}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATE_TONE[item.estado] || "bg-slate-100 text-slate-700"}`}>{quoteStateLabel(item.estado, t)}</span>
          </div>
          <div className="text-sm font-medium text-slate-900">{item.servicio || "—"}</div>
          <div className="text-xs text-slate-500">{fmtMoney(item.precio, item.moneda)} · {item.condiciones || "—"}</div>
        </div>
      </li>
    );
  }
  // task
  const done = item.estado !== "pendiente";
  return (
    <li className={`px-4 py-3 flex gap-3 ${done ? "opacity-60" : ""}`}>
      <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center shrink-0"><ListChecks className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-400">{fmtWhen(item.fecha)}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TASK_TONE[item.tipo] || "bg-slate-100"}`}>{taskTypeLabel(item.tipo, t)}</span>
        </div>
        <div className="text-sm font-medium text-slate-900">{item.detalle || taskTypeLabel(item.tipo, t)}</div>
      </div>
      {!done && (
        <button data-testid={`tl-task-done-${item.id}`} onClick={closeTask} className="text-xs font-semibold px-2 self-center rounded-md bg-emerald-500 text-white">
          <CheckCircle2 className="w-3.5 h-3.5" />
        </button>
      )}
    </li>
  );
}

/* ==================== Call / Quote / Task modals ==================== */
function CallForm({ api, t, lead, onClose, onSaved }) {
  const now = useMemo(() => toLocalDT(new Date()), []);
  const next = useMemo(() => toLocalDT(new Date(Date.now() + 24 * 3600 * 1000)), []);
  const [form, setForm] = useState({
    lead_id: lead.id,
    fecha: now,
    nombre_persona: lead.persona_contacto || "",
    telefono: lead.telefono || "",
    motivo: "cotizacion",
    solicitud: "",
    informacion: "",
    proxima_accion: "",
    proxima_fecha: next,
  });
  const [busy, setBusy] = useState(false);
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/crm/calls", {
        ...form,
        fecha: fromLocalDT(form.fecha),
        proxima_fecha: fromLocalDT(form.proxima_fecha),
      });
      toast.success(t("saved"));
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="crm-call-form">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="font-display font-semibold text-lg">{t("crm_log_call") || "Registrar llamada"}</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
            <b>{lead.nombre_empresa}</b>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>{t("crm_call_when") || "Cuándo"}</label>
              <input type="datetime-local" data-testid="call-fecha" required value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inp} />
            </div>
            <div>
              <label className={lbl}>{t("crm_call_reason") || "Motivo"}</label>
              <select data-testid="call-motivo" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className={inp}>
                <option value="cotizacion">{t("crm_reason_quote") || "Cotización"}</option>
                <option value="seguimiento">{t("crm_reason_followup") || "Seguimiento"}</option>
                <option value="reclamacion">{t("crm_reason_complaint") || "Reclamación"}</option>
                <option value="informacion">{t("crm_reason_info") || "Información"}</option>
                <option value="otro">{t("crm_origin_other") || "Otro"}</option>
              </select>
            </div>
            <div>
              <label className={lbl}>{t("crm_caller") || "Quién llamó"}</label>
              <input data-testid="call-persona" value={form.nombre_persona} onChange={(e) => setForm({ ...form, nombre_persona: e.target.value })} className={inp} />
            </div>
            <div>
              <label className={lbl}>{t("phone")}</label>
              <input data-testid="call-tel" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className={inp} />
            </div>
          </div>
          <div>
            <label className={lbl}>{t("crm_request") || "Qué necesita"}</label>
            <input data-testid="call-solicitud" value={form.solicitud} onChange={(e) => setForm({ ...form, solicitud: e.target.value })} placeholder="Transporte Madrid → París ..." className={inp} />
          </div>
          <div>
            <label className={lbl}>{t("crm_call_notes") || "Información importante"}</label>
            <textarea data-testid="call-info" rows={3} value={form.informacion} onChange={(e) => setForm({ ...form, informacion: e.target.value })} placeholder="Le hemos cotizado 850 €. Consulta internamente." className={inp} />
          </div>
          <div className="border-t pt-3 space-y-3">
            <div className="text-xs font-semibold text-violet-700 uppercase tracking-wide flex items-center gap-1"><Calendar className="w-3 h-3" /> {t("crm_next_step") || "Próximo seguimiento"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={lbl}>{t("crm_action") || "Acción"}</label>
                <input data-testid="call-proxaccion" value={form.proxima_accion} onChange={(e) => setForm({ ...form, proxima_accion: e.target.value })} placeholder="Llamar el viernes" className={inp} />
              </div>
              <div>
                <label className={lbl}>{t("crm_when") || "Cuándo"}</label>
                <input type="datetime-local" data-testid="call-proxfecha" value={form.proxima_fecha} onChange={(e) => setForm({ ...form, proxima_fecha: e.target.value })} className={inp} />
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">{t("cancel")}</button>
          <button type="submit" data-testid="call-save" disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50">{busy ? "..." : t("save")}</button>
        </div>
      </form>
    </div>
  );
}

function QuoteForm({ api, t, lead, onClose, onSaved, initial = null }) {
  const [form, setForm] = useState(initial ? {
    ...initial,
    lead_id: initial.lead_id,
    validez: initial.validez || "",
    tipo_carga: initial.tipo_carga || "kg",
    container_type: initial.container_type || "20ft",
    peso_contenedor: initial.peso_contenedor || 0,
    peso_mercancia: initial.peso_mercancia || 0,
  } : {
    lead_id: lead.id,
    servicio: "", descripcion: "",
    pais_origen: "", ciudad_origen: "",
    pais_destino: "", ciudad_destino: "",
    modo: "aereo", tipo_carga: "kg",
    peso_kg: 0, cbm: 0, bultos: 0,
    container_type: "20ft", peso_contenedor: 0, peso_mercancia: 0,
    precio: 0, moneda: "EUR", condiciones: "",
    validez: new Date(Date.now() + 15 * 86400000).toISOString().slice(0, 10),
    estado: "cotizada",
  });
  const [busy, setBusy] = useState(false);
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  const cargoOptions = useMemo(() => {
    if (form.modo === "maritimo") return [{ v: "cbm", l: t("cargo_cbm") }, { v: "fcl", l: t("fcl") || "FCL" }, { v: "kg", l: t("cargo_kg") }];
    if (form.modo === "terrestre") return [{ v: "pallets", l: t("cargo_pallets") }, { v: "cbm", l: t("cargo_cbm") }, { v: "kg", l: t("cargo_kg") }];
    if (form.modo === "courier") return [{ v: "kg", l: t("cargo_kg") }];
    return [{ v: "kg", l: t("cargo_kg") }, { v: "cbm", l: t("cargo_cbm") }];
  }, [form.modo, t]);

  useEffect(() => {
    if (!cargoOptions.find(o => o.v === form.tipo_carga)) {
      setForm(f => ({ ...f, tipo_carga: cargoOptions[0].v }));
    }
    // eslint-disable-next-line
  }, [form.modo]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial) await api.patch(`/crm/quotes/${initial.id}`, form);
      else await api.post("/crm/quotes", form);
      toast.success(t("saved")); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="crm-quote-form">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h3 className="font-display font-semibold text-lg">{initial ? t("edit") : (t("crm_new_quote") || "Nueva cotización")}</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {lead && <div className="text-xs text-slate-500 bg-slate-50 rounded p-2"><b>{lead.nombre_empresa}</b></div>}
          <div><label className={lbl}>{t("crm_service") || "Servicio"} *</label><input required data-testid="quote-servicio" value={form.servicio} onChange={(e) => setForm({ ...form, servicio: e.target.value })} className={inp} /></div>

          {/* Route data — powers the origin→destination stats */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
            <div className="text-[11px] font-bold text-blue-700 uppercase tracking-wide">{t("crm_route_data") || "Ruta solicitada"}</div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={lbl}>{t("origin_country") || "País origen"}</label><input data-testid="quote-po" value={form.pais_origen} onChange={(e) => setForm({ ...form, pais_origen: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("origin_city") || "Ciudad origen"}</label><input data-testid="quote-co" value={form.ciudad_origen} onChange={(e) => setForm({ ...form, ciudad_origen: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("destination_country") || "País destino"}</label><input data-testid="quote-pd" value={form.pais_destino} onChange={(e) => setForm({ ...form, pais_destino: e.target.value })} className={inp} /></div>
              <div><label className={lbl}>{t("destination_city") || "Ciudad destino"}</label><input data-testid="quote-cd" value={form.ciudad_destino} onChange={(e) => setForm({ ...form, ciudad_destino: e.target.value })} className={inp} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={lbl}>{t("transport") || "Modo"}</label>
                <select data-testid="quote-modo" value={form.modo} onChange={(e) => setForm({ ...form, modo: e.target.value })} className={inp}>
                  <option value="aereo">{t("air") || "Aéreo"}</option>
                  <option value="maritimo">{t("sea") || "Marítimo"}</option>
                  <option value="terrestre">{t("road") || "Terrestre"}</option>
                  <option value="courier">{t("courier") || "Courier"}</option>
                </select>
              </div>
              <div>
                <label className={lbl}>{t("cargo_type") || "Tipo de carga"}</label>
                <select data-testid="quote-tipo-carga" value={form.tipo_carga} onChange={(e) => setForm({ ...form, tipo_carga: e.target.value })} className={inp}>
                  {cargoOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            </div>

            {form.tipo_carga === "fcl" ? (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
                <label className="block text-xs font-semibold text-red-900">{t("container_type") || "Tipo de contenedor"}</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "20ft", l: t("container_20") || "20'" },
                    { v: "40ft", l: t("container_40") || "40'" },
                    { v: "40hc", l: t("container_40hc") || "40 HC" },
                  ].map(c => (
                    <button
                      key={c.v} type="button" data-testid={`quote-container-${c.v}`}
                      onClick={() => setForm({ ...form, container_type: c.v })}
                      className={`px-2 py-2 border-2 rounded-lg text-xs font-semibold transition-all ${
                        form.container_type === c.v ? "border-red-600 bg-white text-red-700" : "border-transparent bg-white/50 hover:bg-white text-slate-600"
                      }`}
                    >{c.l}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={lbl}>{t("container_weight") || "Peso contenedor"}</label>
                    <input type="number" step="0.1" min="0" data-testid="quote-peso-contenedor"
                      value={form.peso_contenedor}
                      onChange={(e) => setForm({ ...form, peso_contenedor: +e.target.value })}
                      className={`${inp} bg-white`} />
                  </div>
                  <div>
                    <label className={lbl}>{t("cargo_weight") || "Peso mercancía"}</label>
                    <input type="number" step="0.1" min="0" data-testid="quote-peso-mercancia"
                      value={form.peso_mercancia}
                      onChange={(e) => setForm({ ...form, peso_mercancia: +e.target.value })}
                      className={`${inp} bg-white`} />
                  </div>
                </div>
                <p className="text-xs text-red-800">
                  {t("total_weight_fcl") || "Peso total"}: <b className="tabular-nums">{((+form.peso_contenedor||0)+(+form.peso_mercancia||0)).toLocaleString()} kg</b>
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                <div><label className={lbl}>{t("weight_kg") || "Peso (kg)"}</label><input type="number" step="0.1" data-testid="quote-peso" value={form.peso_kg} onChange={(e) => setForm({ ...form, peso_kg: +e.target.value })} className={inp} /></div>
                <div><label className={lbl}>CBM</label><input type="number" step="0.01" data-testid="quote-cbm" value={form.cbm} onChange={(e) => setForm({ ...form, cbm: +e.target.value })} className={inp} /></div>
                <div><label className={lbl}>{t("packages") || "Bultos"}</label><input type="number" step="1" data-testid="quote-bultos" value={form.bultos} onChange={(e) => setForm({ ...form, bultos: +e.target.value })} className={inp} /></div>
              </div>
            )}
          </div>

          <div><label className={lbl}>{t("description")}</label><textarea rows={2} data-testid="quote-desc" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className={inp} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className={lbl}>{t("price")} *</label><input required type="number" step="0.01" data-testid="quote-precio" value={form.precio} onChange={(e) => setForm({ ...form, precio: +e.target.value })} className={inp} /></div>
            <div>
              <label className={lbl}>{t("currency")}</label>
              <select data-testid="quote-moneda" value={form.moneda} onChange={(e) => setForm({ ...form, moneda: e.target.value })} className={inp}>
                <option value="EUR">EUR</option><option value="USD">USD</option><option value="IQD">IQD</option><option value="SYP">SYP</option><option value="CNY">CNY</option>
              </select>
            </div>
            <div><label className={lbl}>{t("crm_valid_until") || "Vence"}</label><input type="date" data-testid="quote-validez" value={form.validez} onChange={(e) => setForm({ ...form, validez: e.target.value })} className={inp} /></div>
          </div>
          <div><label className={lbl}>{t("crm_terms") || "Condiciones"}</label><input data-testid="quote-cond" value={form.condiciones} onChange={(e) => setForm({ ...form, condiciones: e.target.value })} className={inp} /></div>
          <div>
            <label className={lbl}>{t("status")}</label>
            <select data-testid="quote-estado" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} className={inp}>
              {["nueva","cotizada","pendiente","seguimiento","aceptada","confirmada","rechazada","perdida","cancelada"].map(s => <option key={s} value={s}>{quoteStateLabel(s, t)}</option>)}
            </select>
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">{t("cancel")}</button>
          <button type="submit" data-testid="quote-save" disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50">{busy ? "..." : t("save")}</button>
        </div>
      </form>
    </div>
  );
}

function TaskForm({ api, t, lead, onClose, onSaved }) {
  const [form, setForm] = useState({
    lead_id: lead.id,
    tipo: "llamar",
    detalle: "",
    fecha: toLocalDT(new Date(Date.now() + 24 * 3600 * 1000)),
  });
  const [busy, setBusy] = useState(false);
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/crm/tasks", { ...form, fecha: fromLocalDT(form.fecha) });
      toast.success(t("saved")); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="crm-task-form">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-4 border-b flex justify-between items-center"><h3 className="font-display font-semibold text-lg">{t("crm_add_task") || "Nuevo seguimiento"}</h3><button type="button" onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-500 bg-slate-50 rounded p-2"><b>{lead.nombre_empresa}</b></div>
          <div><label className={lbl}>{t("crm_task_type") || "Tipo"}</label>
            <select data-testid="task-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} className={inp}>
              {["llamar","enviar_precio","enviar_docs","esperar_confirmacion","confirmar_envio","recontactar","otro"].map(x => <option key={x} value={x}>{taskTypeLabel(x, t)}</option>)}
            </select>
          </div>
          <div><label className={lbl}>{t("crm_task_detail") || "Detalle"}</label><input data-testid="task-detalle" value={form.detalle} onChange={(e) => setForm({ ...form, detalle: e.target.value })} className={inp} /></div>
          <div><label className={lbl}>{t("crm_when") || "Cuándo"}</label><input required type="datetime-local" data-testid="task-fecha" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={inp} /></div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">{t("cancel")}</button>
          <button type="submit" data-testid="task-save" disabled={busy} className="px-4 py-2 text-sm rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold disabled:opacity-50">{busy ? "..." : t("save")}</button>
        </div>
      </form>
    </div>
  );
}

/* ==================== Quotes tab ==================== */
function QuotesTab({ api, t }) {
  const [rows, setRows] = useState([]);
  const [estado, setEstado] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await api.get("/crm/quotes", { params: estado ? { estado } : {} });
      setRows(r.data);
    } catch (err) { console.warn("CRM quotes load failed", err?.response?.status || err?.message); }
  }, [api, estado]);
  useEffect(() => { load(); }, [load]);

  const STATES = ["", "nueva", "cotizada", "pendiente", "seguimiento", "aceptada", "confirmada", "rechazada", "perdida", "cancelada"];

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        {STATES.map(s => (
          <button
            key={s || "all"}
            data-testid={`crm-quote-filter-${s || "all"}`}
            onClick={() => setEstado(s)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full ${estado === s ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
          >
            {s ? quoteStateLabel(s, t) : t("all")}
          </button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">{t("crm_no_quotes") || "Sin cotizaciones."}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-3 text-start">Nº</th>
                  <th className="p-3 text-start">{t("client")}</th>
                  <th className="p-3 text-start">{t("crm_service") || "Servicio"}</th>
                  <th className="p-3 text-end">{t("price")}</th>
                  <th className="p-3 text-start">{t("status")}</th>
                  <th className="p-3 text-end">{t("date")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(q => (
                  <tr key={q.id} data-testid={`crm-quote-${q.id}`} className="hover:bg-slate-50">
                    <td className="p-3 font-mono text-xs">{q.numero}</td>
                    <td className="p-3 font-semibold text-slate-900">{q.lead_nombre}</td>
                    <td className="p-3 text-slate-600 truncate max-w-xs">{q.servicio}</td>
                    <td className="p-3 text-end tabular-nums font-semibold text-slate-900">{fmtMoney(q.precio, q.moneda)}</td>
                    <td className="p-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATE_TONE[q.estado] || "bg-slate-100"}`}>{quoteStateLabel(q.estado, t)}</span></td>
                    <td className="p-3 text-end text-xs text-slate-400">{fmtWhen(q.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== Tasks tab ==================== */
function TasksTab({ api, t }) {
  const [bucket, setBucket] = useState("today");
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { const r = await api.get("/crm/tasks", { params: { estado: "pendiente", bucket } }); setRows(r.data); }
    catch (err) { console.warn("CRM tasks load failed", err?.response?.status || err?.message); }
  }, [api, bucket]);
  useEffect(() => { load(); }, [load]);

  const close = async (id) => {
    try { await api.patch(`/crm/tasks/${id}`, null, { params: { estado: "hecha" } }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const BUCKETS = [
    { k: "today", label: t("crm_tasks_today") || "Hoy" },
    { k: "overdue", label: t("crm_tasks_overdue") || "Atrasados" },
    { k: "upcoming", label: t("crm_tasks_upcoming") || "Próximos 7d" },
  ];

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {BUCKETS.map(b => (
          <button
            key={b.k}
            data-testid={`crm-tasks-bucket-${b.k}`}
            onClick={() => setBucket(b.k)}
            className={`text-sm font-semibold px-4 py-2 rounded-lg ${bucket === b.k ? "bg-red-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
          >{b.label}</button>
        ))}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">{t("crm_no_tasks") || "Sin tareas en este bucket."}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map(x => (
              <li key={x.id} className="px-4 py-3 flex items-center gap-3" data-testid={`crm-task-${x.id}`}>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${TASK_TONE[x.tipo]}`}>{taskTypeLabel(x.tipo, t)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{x.lead_nombre}</div>
                  <div className="text-xs text-slate-500 truncate">{x.detalle} · {fmtWhen(x.fecha)}</div>
                </div>
                <button data-testid={`crm-task-done-${x.id}`} onClick={() => close(x.id)} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white">
                  <CheckCircle2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ==================== Stats tab ==================== */
/**
 * StatsTab — "how many inquiries from X to Y" report.
 *
 * Aggregates all quotes by (origin country → destination country → mode) and
 * shows totals: number of quotes, won / lost / pending split, weight and CBM.
 * Filters: date range and mode.
 */
function StatsTab({ api, t }) {
  const [data, setData] = useState({ rows: [], totals: null });
  const [filters, setFilters] = useState({ desde: "", hasta: "", modo: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (filters.desde) params.desde = filters.desde;
      if (filters.hasta) params.hasta = filters.hasta;
      if (filters.modo) params.modo = filters.modo;
      const r = await api.get("/crm/stats/routes", { params });
      setData(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  }, [api, filters]);
  useEffect(() => { load(); }, [load]);

  const inp = "px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 bg-white";

  const MODES = [
    { v: "", label: t("all") || "Todos" },
    { v: "aereo", label: t("air") || "Aéreo" },
    { v: "maritimo", label: t("sea") || "Marítimo" },
    { v: "terrestre", label: t("road") || "Terrestre" },
    { v: "express", label: t("express") || "Express" },
  ];

  return (
    <div className="space-y-4" data-testid="crm-stats">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{t("from") || "Desde"}</label>
          <input type="date" data-testid="stats-desde" value={filters.desde} onChange={(e) => setFilters({ ...filters, desde: e.target.value })} className={inp} />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{t("to") || "Hasta"}</label>
          <input type="date" data-testid="stats-hasta" value={filters.hasta} onChange={(e) => setFilters({ ...filters, hasta: e.target.value })} className={inp} />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">{t("transport") || "Modo"}</label>
          <select data-testid="stats-modo" value={filters.modo} onChange={(e) => setFilters({ ...filters, modo: e.target.value })} className={inp}>
            {MODES.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </div>
        {(filters.desde || filters.hasta || filters.modo) && (
          <button
            data-testid="stats-clear"
            onClick={() => setFilters({ desde: "", hasta: "", modo: "" })}
            className="text-xs font-semibold text-slate-500 hover:text-red-600 px-3 py-2"
          >
            {t("clear") || "Limpiar"}
          </button>
        )}
      </div>

      {/* Top totals */}
      {data.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard tone="bg-indigo-50 text-indigo-700 border-indigo-200" label={t("crm_stats_total") || "Consultas"} val={data.totals.total} icon={FileText} testid="stats-total" />
          <StatCard tone="bg-emerald-50 text-emerald-700 border-emerald-200" label={t("crm_quotes_won") || "Aceptadas"} val={data.totals.ganadas} icon={CheckCircle2} testid="stats-won" />
          <StatCard tone="bg-amber-50 text-amber-800 border-amber-200" label={t("crm_quotes_pending") || "Pendientes"} val={data.totals.pendientes} icon={Clock} testid="stats-pending" />
          <StatCard tone="bg-slate-100 text-slate-600 border-slate-200" label={t("crm_quotes_lost") || "Perdidas"} val={data.totals.perdidas} icon={X} testid="stats-lost" />
        </div>
      )}

      {/* Aggregated routes table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-red-600" />
          <h3 className="font-display font-semibold text-sm text-slate-900">{t("crm_stats_by_route") || "Consultas por ruta"}</h3>
          {busy && <span className="text-xs text-slate-400 ms-auto">…</span>}
        </div>
        {data.rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="stats-empty">
            {t("crm_stats_empty") || "No hay cotizaciones con datos de ruta en el rango seleccionado."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="p-3 text-start">{t("origin") || "Origen"}</th>
                  <th className="p-3 text-start">{t("destination") || "Destino"}</th>
                  <th className="p-3 text-start">{t("transport") || "Modo"}</th>
                  <th className="p-3 text-end">{t("crm_stats_total") || "Consultas"}</th>
                  <th className="p-3 text-end text-emerald-600">{t("crm_quotes_won") || "Ganadas"}</th>
                  <th className="p-3 text-end text-amber-600">{t("crm_quotes_pending") || "Pend."}</th>
                  <th className="p-3 text-end text-rose-500">{t("crm_quotes_lost") || "Perd."}</th>
                  <th className="p-3 text-end">{t("weight_kg") || "Peso kg"}</th>
                  <th className="p-3 text-end">CBM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.rows.map((r, i) => (
                  <tr key={`${r.pais_origen}-${r.pais_destino}-${r.modo}-${i}`} data-testid={`stats-row-${i}`} className="hover:bg-slate-50">
                    <td className="p-3 font-semibold text-slate-900">{r.pais_origen}</td>
                    <td className="p-3 font-semibold text-slate-900">{r.pais_destino}</td>
                    <td className="p-3 text-xs text-slate-500 uppercase tracking-wider">{r.modo}</td>
                    <td className="p-3 text-end tabular-nums font-bold">{r.total}</td>
                    <td className="p-3 text-end tabular-nums text-emerald-700">{r.ganadas}</td>
                    <td className="p-3 text-end tabular-nums text-amber-600">{r.pendientes}</td>
                    <td className="p-3 text-end tabular-nums text-rose-500">{r.perdidas}</td>
                    <td className="p-3 text-end tabular-nums text-slate-600">{r.peso_total}</td>
                    <td className="p-3 text-end tabular-nums text-slate-600">{r.cbm_total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ tone, label, val, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className={`p-3 rounded-xl border-2 ${tone}`}>
      <div className="flex items-center justify-between mb-2"><Icon className="w-4 h-4 opacity-70" /></div>
      <div className="text-2xl font-display font-bold">{val}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

